
import { useAuthStore } from '@/lib/stores/auth';
import { canAccessFeature, type RoleName } from '@/lib/utils/role-access';

/**
 * Custom hook for authentication and permission checking
 *
 * Provides a simple interface for checking permissions based on JWT roles.
 * Now uses JWT-based role extraction instead of API calls.
 *
 * Usage:
 * ```tsx
 * const { can } = usePermissions();
 *
 * if (can('manage_users')) return <AdminPanel />;
 * if (can('create_stream')) return <CreateStreamButton />;
 * ```
 */
export function usePermissions() {
  const {
    user,
    isAuthenticated,
    isLoading,
    isInitialized
  } = useAuthStore();

  const userRole = user?.role?.name as RoleName | undefined;

  // Helper logic
  const hasRole = (role: RoleName) => userRole === role;
  const isAdmin = hasRole('admin');
  const isModerator = hasRole('moderator');

  const isMediaTeam = hasRole('media_team');
  const canManageUsers = isAdmin;
  const canManageStreams = isAdmin || isMediaTeam;
  const canModerateContent = isAdmin || isModerator;

  // Main permission checking function as specified in the plan
  const can = (permission: string) => {
    return userRole ? canAccessFeature(permission, userRole) : false;
  };

  // Keep backwards compatibility with existing usage
  const hasValidRole = isAuthenticated && !!userRole;
  const isLoadingUserData = false; // No longer needed with JWT-based roles

  return {
    // Simple permission interface (new)
    can,

    // Backwards compatibility (existing usage)
    user,
    isAuthenticated,
    isLoading,
    isInitialized,
    hasValidRole,
    isLoadingUserData,
    isAdmin,
    canManageStreams,
    canModerate: canModerateContent,
    canManageUsers,
    userRole,
    userId: user?.id,
    userName: user?.name,
    userEmail: user?.email,
    canAccessFeature: can, // Alias for backwards compatibility
    hasRole: (roleName: string) => userRole === roleName,
  };
}